import { createPortal } from "react-dom";
import { YStack, View } from "tamagui";
import { colors } from "@styles/colors";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * 輕量底部彈出層：半透明遮罩 + 置中（與 App 容器同寬 430）面板。
 *
 * 走 createPortal 掛到 document.body，不留在呼叫端的 DOM 位置。
 * 原因：position: fixed 的定位基準不一定是 viewport——只要有祖先建立了「包含區塊」
 * （transform / filter / will-change / contain 任一個都會），fixed 就改以那個祖先為準。
 * Tamagui 的 ScrollView 在 web 上會為捲動效能建立合成層，所以遮罩原本只蓋到內容區，
 * 釘住的標題列露在外面沒被遮，交界還跟著捲動容器的圓角被裁出一道邊。
 * Portal 之後遮罩才真的是整個視窗（帳號區的編輯姓名彈窗也是同一套做法）。
 */
export default function BottomSheet({ open, onClose, children }: BottomSheetProps) {
  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <View
        onPress={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100040, animation: "fadeIn 0.2s ease" }}
      />
      <YStack
        bg={colors.light}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          maxWidth: 430,
          margin: "0 auto",
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          // 100040/100041：與 UserProfileHeader 的編輯彈窗（100050）同一個層級帶，
          // 都要蓋過釘住的標題列與浮起的 tab bar。散在各處寫小數字（原本是 50）
          // 只有在「沒有其他固定元素」時才成立。
          zIndex: 100041,
          maxHeight: "85vh",
          overflowY: "auto",
          animation: "fadeUp 0.3s ease",
        }}>
        <View w={40} h={4} br={2} bg={colors.border} style={{ margin: "10px auto 14px" }} />
        {children}
      </YStack>
    </>,
    document.body,
  );
}
